import React, { useEffect, useId, useState } from "react";

import {
  createEmptyDetailSection,
  moveListItem,
  splitMultilineList,
} from "../../../../utils/catalogProductStudioShared";
import { fill, formatMoney, getCoverImage } from "../studioEditModel";
import { Btn, Card, Chip, Field, Icon, TextInput } from "../StudioUi";

const splitLines = (text) => (text ? String(text).split("\n") : []);

/**
 * Editable list stored as newline-separated text (the existing form format,
 * so buildEnrichmentPayload stays untouched). Keeps empty rows locally while
 * the person is typing.
 */
const ListEditor = ({ value, onChange, maxLength, placeholder, addLabel, removeLabel, upLabel, downLabel, idPrefix }) => {
  const [items, setItems] = useState(() => splitLines(value));
  useEffect(() => {
    if ((value || "") !== items.join("\n")) setItems(splitLines(value));
    // Only react to outside changes (discard, hydration).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const commit = (next) => {
    setItems(next);
    onChange(next.join("\n"));
  };

  return (
    <div className="pse-list-editor">
      {items.map((item, index) => (
        <div key={`${idPrefix}-${index}`} className="pse-list-editor__row">
          <Icon name="grip" size={16} className="pse-list-editor__grip" />
          <TextInput
            id={`${idPrefix}-${index}`}
            aria-label={`${placeholder} ${index + 1}`}
            value={item}
            maxLength={maxLength}
            placeholder={placeholder}
            onChange={(next) => commit(items.map((entry, entryIndex) => (entryIndex === index ? next.replace(/\n/g, " ") : entry)))}
          />
          <div className="pse-list-editor__controls">
            <button
              type="button"
              className="pse-icon-btn pse-icon-btn--sm"
              aria-label={upLabel}
              disabled={index === 0}
              onClick={() => commit(moveListItem(items, index, -1))}
            >
              <Icon name="chevronDown" size={15} className="pse-rotate-180" />
            </button>
            <button
              type="button"
              className="pse-icon-btn pse-icon-btn--sm"
              aria-label={downLabel}
              disabled={index === items.length - 1}
              onClick={() => commit(moveListItem(items, index, 1))}
            >
              <Icon name="chevronDown" size={15} />
            </button>
            <button
              type="button"
              className="pse-icon-btn pse-icon-btn--sm pse-icon-btn--danger"
              aria-label={removeLabel}
              onClick={() => commit(items.filter((_entry, entryIndex) => entryIndex !== index))}
            >
              <Icon name="trash" size={15} />
            </button>
          </div>
        </div>
      ))}
      <Btn variant="dashed" icon="plus" onClick={() => commit([...items, ""])}>
        {addLabel}
      </Btn>
    </div>
  );
};

const BadgeEditor = ({ value, onChange, tt, idPrefix }) => {
  const [draft, setDraft] = useState("");
  const badges = splitMultilineList(value);
  const add = () => {
    const next = draft.trim();
    if (!next || badges.includes(next)) return;
    onChange([...badges, next].join("\n"));
    setDraft("");
  };
  return (
    <div className="pse-badge-editor">
      {badges.length ? (
        <ul className="pse-badge-editor__list">
          {badges.map((badge, index) => (
            <li key={`${badge}-${index}`} className="pse-badge-editor__item">
              <Icon name="tag" size={15} />
              <span>{badge}</span>
              <button
                type="button"
                className="pse-icon-btn pse-icon-btn--sm"
                aria-label={`${tt("remove")}: ${badge}`}
                onClick={() => onChange(badges.filter((_entry, entryIndex) => entryIndex !== index).join("\n"))}
              >
                <Icon name="x" size={14} />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="pse-muted pse-small">{tt("dtBadgesEmpty")}</p>
      )}
      <div className="pse-badge-editor__add">
        <TextInput
          id={`${idPrefix}-new`}
          aria-label={tt("dtAddBadge")}
          value={draft}
          maxLength={80}
          placeholder={tt("dtBadgePlaceholder")}
          onChange={setDraft}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              add();
            }
          }}
        />
        <Btn variant="secondary" icon="plus" onClick={add} disabled={!draft.trim()}>
          {tt("dtAddBadge")}
        </Btn>
      </div>
    </div>
  );
};

const DetailsTab = ({ studio }) => {
  const { tt, coreForm, pricingForm, mediaDrafts, dirty, updateEnrichment, selectTab, workspace, brands, merchandisingTypes, productFamilyOptions } =
    studio;
  const ids = useId();
  const enrichment = coreForm.enrichment;
  const sections = enrichment.detailSections || [];
  const highlights = splitMultilineList(enrichment.highlightsText);
  const badges = splitMultilineList(enrichment.badgesText);
  const cover = getCoverImage(mediaDrafts);

  const setSections = (next) => updateEnrichment("detailSections", next);
  const updateSection = (index, field, value) =>
    setSections(sections.map((section, sectionIndex) => (sectionIndex === index ? { ...section, [field]: value } : section)));

  const counter = (value, max) => fill(tt("charCount"), { count: String(value || "").length, max });
  const brandName =
    brands.find((brand) => String(brand.id) === String(coreForm.brandId))?.name || workspace.product.brandName || tt("notSet");
  const typeName =
    merchandisingTypes.find((type) => String(type.id) === String(coreForm.merchandisingTypeId))?.name ||
    workspace.product.merchandisingTypeName ||
    tt("notSet");
  const familyName = productFamilyOptions.find((option) => option.value === coreForm.productFamily)?.label || coreForm.productFamily;

  const infoRows = [
    ["dtMaterial", enrichment.material],
    ["dtComposition", enrichment.composition],
    ["dtCare", enrichment.careInstructions],
    ["dtFit", enrichment.fitNotes],
    ["dtSizeGuide", enrichment.sizeGuideNotes],
    ["dtStyling", enrichment.stylingNotes],
  ].filter(([, value]) => String(value || "").trim());
  const completeSections = sections.filter((section) => section.title.trim() && section.body.trim());

  return (
    <div className="pse-layout pse-layout--details">
      <div className="pse-layout__main">
        <div className="pse-grid pse-grid--details-top">
          <Card
            title={tt("dtStory")}
            subtitle={tt("dtStoryCopy")}
            actions={dirty.core ? <Chip tone="warning">{tt("unsavedChip")}</Chip> : null}
          >
            <Field htmlFor={`${ids}-story`} counter={counter(enrichment.productStory, 2000)}>
              <TextInput
                id={`${ids}-story`}
                multiline
                rows={7}
                maxLength={2000}
                value={enrichment.productStory}
                placeholder={tt("dtStoryPlaceholder")}
                aria-label={tt("dtStory")}
                onChange={(value) => updateEnrichment("productStory", value)}
              />
            </Field>
          </Card>

          <Card title={tt("dtHighlights")} subtitle={tt("dtHighlightsCopy")}>
            <ListEditor
              idPrefix={`${ids}-highlight`}
              value={enrichment.highlightsText}
              onChange={(value) => updateEnrichment("highlightsText", value)}
              maxLength={140}
              placeholder={tt("dtHighlightPlaceholder")}
              addLabel={tt("dtAddHighlight")}
              removeLabel={tt("remove")}
              upLabel={tt("moveEarlier")}
              downLabel={tt("moveLater")}
            />
          </Card>
        </div>

        <div className="pse-grid pse-grid--details-mid">
          <Card title={tt("dtInfo")} subtitle={tt("dtInfoCopy")}>
            <Field label={tt("dtMaterial")} htmlFor={`${ids}-material`} counter={counter(enrichment.material, 255)}>
              <TextInput
                id={`${ids}-material`}
                maxLength={255}
                value={enrichment.material}
                placeholder={tt("dtMaterialPlaceholder")}
                onChange={(value) => updateEnrichment("material", value)}
              />
            </Field>
            <div className="pse-grid pse-grid--2">
              <Field label={tt("dtComposition")} htmlFor={`${ids}-composition`} counter={counter(enrichment.composition, 1000)}>
                <TextInput
                  id={`${ids}-composition`}
                  multiline
                  rows={3}
                  maxLength={1000}
                  value={enrichment.composition}
                  placeholder={tt("dtCompositionPlaceholder")}
                  onChange={(value) => updateEnrichment("composition", value)}
                />
              </Field>
              <Field label={tt("dtCare")} htmlFor={`${ids}-care`} counter={counter(enrichment.careInstructions, 1000)}>
                <TextInput
                  id={`${ids}-care`}
                  multiline
                  rows={3}
                  maxLength={1000}
                  value={enrichment.careInstructions}
                  placeholder={tt("dtCarePlaceholder")}
                  onChange={(value) => updateEnrichment("careInstructions", value)}
                />
              </Field>
            </div>
            <div className="pse-grid pse-grid--2">
              <Field label={tt("dtFit")} htmlFor={`${ids}-fit`} counter={counter(enrichment.fitNotes, 1000)}>
                <TextInput
                  id={`${ids}-fit`}
                  multiline
                  rows={2}
                  maxLength={1000}
                  value={enrichment.fitNotes}
                  placeholder={tt("dtFitPlaceholder")}
                  onChange={(value) => updateEnrichment("fitNotes", value)}
                />
              </Field>
              <Field label={tt("dtSizeGuide")} htmlFor={`${ids}-size`} counter={counter(enrichment.sizeGuideNotes, 1000)}>
                <TextInput
                  id={`${ids}-size`}
                  multiline
                  rows={2}
                  maxLength={1000}
                  value={enrichment.sizeGuideNotes}
                  placeholder={tt("dtSizeGuidePlaceholder")}
                  onChange={(value) => updateEnrichment("sizeGuideNotes", value)}
                />
              </Field>
            </div>
          </Card>

          <div className="pse-stack">
            <Card title={tt("dtBadges")} subtitle={tt("dtBadgesCopy")}>
              <BadgeEditor
                tt={tt}
                idPrefix={`${ids}-badge`}
                value={enrichment.badgesText}
                onChange={(value) => updateEnrichment("badgesText", value)}
              />
            </Card>
            <Card title={tt("dtStyling")} subtitle={tt("dtStylingCopy")}>
              <Field htmlFor={`${ids}-styling`} counter={counter(enrichment.stylingNotes, 1000)}>
                <TextInput
                  id={`${ids}-styling`}
                  multiline
                  rows={3}
                  maxLength={1000}
                  aria-label={tt("dtStyling")}
                  value={enrichment.stylingNotes}
                  placeholder={tt("dtStylingPlaceholder")}
                  onChange={(value) => updateEnrichment("stylingNotes", value)}
                />
              </Field>
            </Card>
          </div>
        </div>

        <Card
          title={tt("dtSections")}
          subtitle={tt("dtSectionsCopy")}
          actions={
            <Btn variant="secondary" icon="plus" onClick={() => setSections([...sections, createEmptyDetailSection()])}>
              {tt("dtAddSection")}
            </Btn>
          }
        >
          {sections.length ? (
            <div className="pse-section-list">
              {sections.map((section, index) => {
                const incomplete = !(section.title.trim() && section.body.trim());
                return (
                  <div key={`section-${index}`} className="pse-section-editor">
                    <div className="pse-section-editor__fields">
                      <Field label={tt("dtSectionTitle")} htmlFor={`${ids}-section-title-${index}`} counter={counter(section.title, 80)}>
                        <TextInput
                          id={`${ids}-section-title-${index}`}
                          maxLength={80}
                          value={section.title}
                          placeholder={tt("dtSectionTitlePlaceholder")}
                          onChange={(value) => updateSection(index, "title", value)}
                        />
                      </Field>
                      <Field label={tt("dtSectionBody")} htmlFor={`${ids}-section-body-${index}`} counter={counter(section.body, 1000)}>
                        <TextInput
                          id={`${ids}-section-body-${index}`}
                          multiline
                          rows={3}
                          maxLength={1000}
                          value={section.body}
                          placeholder={tt("dtSectionBodyPlaceholder")}
                          onChange={(value) => updateSection(index, "body", value)}
                        />
                      </Field>
                      {incomplete ? <p className="pse-field__error">{tt("dtSectionIncomplete")}</p> : null}
                    </div>
                    <div className="pse-section-editor__controls">
                      <button
                        type="button"
                        className="pse-icon-btn pse-icon-btn--sm"
                        aria-label={tt("moveEarlier")}
                        disabled={index === 0}
                        onClick={() => setSections(moveListItem(sections, index, -1))}
                      >
                        <Icon name="chevronDown" size={15} className="pse-rotate-180" />
                      </button>
                      <button
                        type="button"
                        className="pse-icon-btn pse-icon-btn--sm"
                        aria-label={tt("moveLater")}
                        disabled={index === sections.length - 1}
                        onClick={() => setSections(moveListItem(sections, index, 1))}
                      >
                        <Icon name="chevronDown" size={15} />
                      </button>
                      <button
                        type="button"
                        className="pse-icon-btn pse-icon-btn--sm pse-icon-btn--danger"
                        aria-label={tt("remove")}
                        onClick={() => setSections(sections.filter((_section, sectionIndex) => sectionIndex !== index))}
                      >
                        <Icon name="trash" size={15} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="pse-muted">{tt("dtSectionsEmpty")}</p>
          )}
        </Card>
      </div>

      <aside className="pse-layout__side">
        <Card title={tt("dtPreview")} subtitle={tt("dtPreviewNote")} className="pse-card--sticky">
          <div className="pse-shop-preview">
            <div className="pse-shop-preview__image">
              {cover?.sourceUrl ? <img src={cover.sourceUrl} alt="" /> : <Icon name="image" size={30} />}
            </div>
            <div className="pse-shop-preview__body">
              {badges.length ? (
                <div className="pse-chip-row">
                  {badges.map((badge) => (
                    <Chip key={badge}>{badge}</Chip>
                  ))}
                </div>
              ) : null}
              <strong className="pse-shop-preview__title">{coreForm.title || "—"}</strong>
              <span className="pse-shop-preview__price">{formatMoney(pricingForm.price)}</span>
              {enrichment.productStory ? <p className="pse-shop-preview__story">{enrichment.productStory}</p> : null}
              {highlights.length ? (
                <ul className="pse-shop-preview__highlights">
                  {highlights.map((highlight) => (
                    <li key={highlight}>
                      <Icon name="check" size={14} strokeWidth={2.4} />
                      {highlight}
                    </li>
                  ))}
                </ul>
              ) : null}
              {infoRows.length || completeSections.length ? (
                <div className="pse-shop-preview__accordion">
                  {infoRows.map(([key, value]) => (
                    <details key={key}>
                      <summary>{tt(key)}</summary>
                      <p>{value}</p>
                    </details>
                  ))}
                  {completeSections.map((section, index) => (
                    <details key={`${section.title}-${index}`}>
                      <summary>{section.title}</summary>
                      <p>{section.body}</p>
                    </details>
                  ))}
                </div>
              ) : null}
              {!enrichment.productStory && !highlights.length && !infoRows.length && !completeSections.length ? (
                <p className="pse-muted pse-small">{tt("dtPreviewEmpty")}</p>
              ) : null}
            </div>
          </div>
        </Card>

        <Card
          title={tt("dtClassification")}
          subtitle={tt("dtClassificationCopy")}
          actions={
            <Btn variant="ghost" size="sm" iconRight="arrowRight" onClick={() => selectTab("overview")}>
              {tt("dtEditInOverview")}
            </Btn>
          }
        >
          <div className="pse-kv-list">
            <div className="pse-kv">
              <span className="pse-kv__label">{tt("ovBrand")}</span>
              <span className="pse-kv__value">{brandName}</span>
            </div>
            <div className="pse-kv">
              <span className="pse-kv__label">{tt("ovCategory")}</span>
              <span className="pse-kv__value">{familyName}</span>
            </div>
            <div className="pse-kv">
              <span className="pse-kv__label">{tt("ovType")}</span>
              <span className="pse-kv__value">{typeName}</span>
            </div>
          </div>
        </Card>
      </aside>
    </div>
  );
};

export default DetailsTab;
